import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Trash2, Tag, AlertCircle, Link2, Search, ToggleLeft } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import UserManagement from './UserManagement';
import LocationsManagement from './LocationsManagement';
import { StickerTypesPanel } from './StickerTypesPanel';

const SettingsPage = () => {
  const { isAdmin } = useAuth();
  const [categories, setCategories] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMappingDialogOpen, setIsMappingDialogOpen] = useState(false);
  const [searchMapping, setSearchMapping] = useState('');
  const [formData, setFormData] = useState({ name: '', type: 'recipe_category' });
  const [mappingForm, setMappingForm] = useState({ import_name: '', recipe_id: '', recipe_name: '' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [catRes, mapRes, recRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/recipe-mappings`),
        axios.get(`${API}/recipes`)
      ]);
      setCategories(catRes.data);
      setMappings(mapRes.data);
      setRecipes(recRes.data);
    } catch (error) {
      toast.error('Failed to fetch settings data');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/categories`, null, { params: { name: formData.name, type: formData.type } });
      toast.success('Category created');
      setIsDialogOpen(false);
      setFormData({ name: '', type: 'recipe_category' });
      fetchData();
    } catch (error) { toast.error('Failed to create category'); }
  };

  const handleDeleteCategory = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await axios.delete(`${API}/categories/${id}`);
      toast.success('Category deleted');
      fetchData();
    } catch (error) { toast.error('Failed to delete category'); }
  };

  const handleToggleSizeVariations = async (catId, currentValue) => {
    try {
      await axios.put(`${API}/categories/${catId}`, { has_size_variations: !currentValue });
      toast.success('Updated size variation setting');
      fetchData();
    } catch (error) { toast.error('Failed to update category'); }
  };

  const handleMappingSubmit = async (e) => {
    e.preventDefault();
    if (!mappingForm.import_name || !mappingForm.recipe_id) {
      toast.error('Please fill both fields');
      return;
    }
    try {
      await axios.post(`${API}/recipe-mappings`, mappingForm);
      toast.success('Mapping saved');
      setIsMappingDialogOpen(false);
      setMappingForm({ import_name: '', recipe_id: '', recipe_name: '' });
      fetchData();
    } catch (error) { toast.error('Failed to save mapping'); }
  };

  const handleDeleteMapping = async (id) => {
    try {
      await axios.delete(`${API}/recipe-mappings/${id}`);
      toast.success('Mapping deleted');
      fetchData();
    } catch (error) { toast.error('Failed to delete mapping'); }
  };

  const recipeCategories = categories.filter(c => c.type === 'recipe_category');
  const mealTypes = categories.filter(c => c.type === 'meal_type');
  const stickerTypes = categories.filter(c => c.type === 'sticker_type').sort((a, b) => a.name.localeCompare(b.name));
  const filteredMappings = mappings.filter(m => 
    !searchMapping || m.import_name.toLowerCase().includes(searchMapping.toLowerCase()) || 
    m.recipe_name.toLowerCase().includes(searchMapping.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8">
        <div className="card p-12 text-center" style={{ borderRadius: '12px' }}>
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            Admin Access Required
          </h2>
          <p className="text-gray-500">Only administrators can access settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8" data-testid="settings-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl tracking-tight font-bold text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>
          Settings
        </h1>
        <p className="text-sm text-gray-500 mt-1">Manage categories, recipe mappings, and system configuration</p>
      </div>

      {loading && <div className="progress-bar mb-4"><div className="progress-bar-fill"></div></div>}

      <Tabs defaultValue="categories">
        <TabsList className="mb-6" style={{ borderRadius: '8px' }}>
          <TabsTrigger value="categories" style={{ borderRadius: '6px' }}>
            <Tag className="w-4 h-4 mr-2" /> Categories
          </TabsTrigger>
          <TabsTrigger value="mappings" style={{ borderRadius: '6px' }}>
            <Link2 className="w-4 h-4 mr-2" /> Recipe Mappings
          </TabsTrigger>
        </TabsList>

        {/* CATEGORIES TAB */}
        <TabsContent value="categories">
          <div className="card" style={{ borderRadius: '12px' }}>
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>Categories</h2>
              <Button onClick={() => setIsDialogOpen(true)} className="btn-primary" data-testid="add-category-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Category
              </Button>
            </div>

            <div className="p-5">
              <Tabs defaultValue="recipe_category">
                <TabsList className="mb-4" style={{ borderRadius: '8px' }}>
                  <TabsTrigger value="recipe_category" style={{ borderRadius: '6px' }}>
                    Recipe Categories ({recipeCategories.length})
                  </TabsTrigger>
                  <TabsTrigger value="meal_type" style={{ borderRadius: '6px' }}>
                    Meal Types ({mealTypes.length})
                  </TabsTrigger>
                  <TabsTrigger value="sticker_type" style={{ borderRadius: '6px' }} data-testid="sticker-type-tab">
                    Sticker Type ({stickerTypes.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="recipe_category">
                  <p className="text-sm text-gray-500 mb-4">Group Final Recipes (e.g., Heat to Eat, Chilled, Weekly Specials).</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {recipeCategories.map((cat) => (
                      <div key={cat.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow" data-testid={`category-${cat.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full bg-[var(--color-primary)]"></div>
                          <div>
                            <span className="font-medium text-sm">{cat.name}</span>
                            <div className="flex items-center gap-2 mt-1">
                              <Switch
                                checked={!!cat.has_size_variations}
                                onCheckedChange={() => handleToggleSizeVariations(cat.id, cat.has_size_variations)}
                                className="scale-75"
                                data-testid={`size-var-toggle-${cat.id}`}
                              />
                              <span className="text-[10px] text-gray-500 uppercase tracking-wider">
                                {cat.has_size_variations ? 'Size Variations ON' : 'Standard Only'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <button onClick={() => handleDeleteCategory(cat.id, cat.name)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`delete-category-${cat.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    {recipeCategories.length === 0 && <div className="col-span-full text-center py-8 text-gray-500">No recipe categories found.</div>}
                  </div>
                </TabsContent>

                <TabsContent value="meal_type">
                  <p className="text-sm text-gray-500 mb-4">Classify recipes by dietary requirements (e.g., Standard, Vegetarian).</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {mealTypes.map((cat) => (
                      <div key={cat.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow" data-testid={`category-${cat.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                          <span className="font-medium text-sm">{cat.name}</span>
                        </div>
                        <button onClick={() => handleDeleteCategory(cat.id, cat.name)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`delete-category-${cat.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    {mealTypes.length === 0 && <div className="col-span-full text-center py-8 text-gray-500">No meal types found.</div>}
                  </div>
                </TabsContent>

                <TabsContent value="sticker_type">
                  <StickerTypesPanel stickers={stickerTypes} onChanged={fetchData} />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </TabsContent>

        {/* RECIPE MAPPINGS TAB */}
        <TabsContent value="mappings">
          <div className="card" style={{ borderRadius: '12px' }}>
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>Recipe Mappings</h2>
                <p className="text-sm text-gray-500 mt-1">Map imported dish names to existing recipes for auto-linking during order import.</p>
              </div>
              <Button onClick={() => setIsMappingDialogOpen(true)} className="btn-primary" data-testid="add-mapping-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Mapping
              </Button>
            </div>

            <div className="p-5">
              <div className="relative mb-4 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search mappings..."
                  value={searchMapping}
                  onChange={(e) => setSearchMapping(e.target.value)}
                  className="pl-10"
                  data-testid="search-mappings"
                />
              </div>

              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Import Name (from Excel)</th>
                      <th>Linked Recipe</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMappings.map(m => (
                      <tr key={m.id}>
                        <td className="font-medium text-sm">{m.import_name}</td>
                        <td className="text-sm text-green-600 flex items-center gap-1">
                          <Link2 className="w-3 h-3" /> {m.recipe_name}
                        </td>
                        <td>
                          <button onClick={() => handleDeleteMapping(m.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredMappings.length === 0 && (
                      <tr><td colSpan={3} className="text-center py-8 text-gray-400">
                        {mappings.length === 0 ? 'No mappings yet. Mappings are auto-created during order import, or add manually.' : 'No matches found.'}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Category Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[400px]" style={{ borderRadius: '12px' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>Add New Category</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCategorySubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Category Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Sandwiches"
                  required
                  data-testid="category-name-input"
                />
              </div>
              <div className="grid gap-2">
                <Label>Type</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                  <SelectTrigger data-testid="category-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recipe_category">Recipe Category</SelectItem>
                    <SelectItem value="meal_type">Meal Type</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" onClick={() => setIsDialogOpen(false)} className="btn-outline">Cancel</Button>
              <Button type="submit" className="btn-primary" data-testid="save-category-btn">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Mapping Dialog */}
      <Dialog open={isMappingDialogOpen} onOpenChange={setIsMappingDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" style={{ borderRadius: '12px' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>Add Recipe Mapping</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleMappingSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Import Name (as it appears in Excel)</Label>
                <Input
                  value={mappingForm.import_name}
                  onChange={(e) => setMappingForm({ ...mappingForm, import_name: e.target.value })}
                  placeholder="e.g., Cheddar & Honey Carrot Sandwich"
                  required
                  data-testid="mapping-import-name"
                />
              </div>
              <div className="grid gap-2">
                <Label>Link to Recipe</Label>
                <Select value={mappingForm.recipe_id || "none"} onValueChange={(v) => {
                  const recipe = recipes.find(r => r.id === v);
                  setMappingForm({ ...mappingForm, recipe_id: v === "none" ? "" : v, recipe_name: recipe?.name || '' });
                }}>
                  <SelectTrigger data-testid="mapping-recipe-select"><SelectValue placeholder="Select recipe..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Select --</SelectItem>
                    {recipes.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" onClick={() => setIsMappingDialogOpen(false)} className="btn-outline">Cancel</Button>
              <Button type="submit" className="btn-primary" data-testid="save-mapping-btn">Save Mapping</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Locations Management Section */}
      <div className="mt-8 border-t border-gray-200 pt-8">
        <LocationsManagement />
      </div>

      {/* User Management Section */}
      <div className="mt-8 border-t border-gray-200 pt-8">
        <UserManagement />
      </div>
    </div>
  );
};

export default SettingsPage;
